import { Grid, GridCustomCellProps, GridColumn as Column, GridDetailRowProps, GridPageChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumnProps } from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from "@progress/kendo-react-indicators";
import { useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import { fetchApi } from "../../services/api";
import { dateFormatter } from "../../utils/utils";
import { QuoteHistoryDetailViewModel } from "../../TypeGen/Customer/QuoteHistoryDetail/quote-history-detail-view-model";
import { QuoteHistoryViewModel } from "../../TypeGen/Customer/QuoteHistory/quote-history-view-model";
import DateCell from "../../components/cells/DateCell";
import ThreadIDCell from "../Quote/Recent/ThreadIDCell";

interface QuoteDetailProps extends GridDetailRowProps {
  customerId: string;
  useBillTo: boolean;
  dataItem: QuoteHistoryViewModel;
}

const PAGE_SIZE = 8;

const EXPORT_COLUMNS: ExcelExportColumnProps[] = [
  { field: "QuoteID", title: "Quote #", width: 70 },
  { field: "Status", title: "Status" },
  { field: "ThreadID", title: "Front" },
  { field: "Source", title: "Source" },
  { field: "Total", title: "Total Charges", cellOptions: { format: "$#,##0.00" } },
  { field: "Bid", title: "Bid" },
  { field: "Declined", title: "Declined" },
  { field: "OrderNumber", title: "Order #", width: 70 },
  { field: "DeclineReason", title: "Decline Reason" },
  { field: "VehicleTypeName", title: "Vehicle Type" },
  { field: "FromLocation", title: "From" },
  { field: "ToLocation", title: "To" },
  { field: "ShipDateTime", title: "Ship Date" },
  { field: "CreatedDateTime", title: "Created" },
  { field: "CreatedByUserName", title: "By" }
];

const QuoteHistoryDetail = ({ dataItem, customerId, useBillTo }: QuoteDetailProps) => {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState<QuoteHistoryDetailViewModel[]>([]);
  const [skip, setSkip] = useState(0);
  const excelExport = useRef<ExcelExport | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchApi(`/api/Customer/CustomerQuoteHistoryDetail`,
      {
        CustomerID: customerId,
        YYYYMM: dataItem.YYYYMM,
        UseBillToCustomer: useBillTo
      }, 'POST')
      .then((response) => setQuotes(response.Quotes))
      .catch(() => alert('Unable to load quote details.'))
      .finally(() => setLoading(false));
  }, [customerId, dataItem.YYYYMM, useBillTo]);

  const QuoteIDCell = (props: GridCustomCellProps) => {
    const quote: QuoteHistoryDetailViewModel = props.dataItem;

    return (
      <td {...props.tdProps}>
        <Link style={{ color: '#007bff' }} to={`/Quote/Index/${quote.QuoteID}`}>{quote.QuoteID}</Link>
      </td>
    );
  };

  const BidCell = (props: GridCustomCellProps) => {
    const quote: QuoteHistoryDetailViewModel = props.dataItem;

    return <td {...props.tdProps}>{[quote.Bid && 'Bid', quote.Declined && 'Decline'].filter(Boolean).join(' / ')}</td>;
  };

  const ResultCell = (props: GridCustomCellProps) => {
    const quote: QuoteHistoryDetailViewModel = props.dataItem;

    if (quote.OrderID) {
      return (
        <td {...props.tdProps}>
          <Link style={{ color: '#007bff' }} to={`/Order/${quote.OrderID}`}>{quote.OrderNumber || "View"}</Link>
        </td>
      );
    }

    return <td {...props.tdProps}>{quote.DeclineReason}</td>;
  };

  const exportToExcel = () => {
    const exportData = quotes.map((quote) => ({
      ...quote,
      ShipDateTime: quote.ShipDateTime ? dateFormatter(quote.ShipDateTime) : '',
      CreatedDateTime: quote.CreatedDateTime ? dateFormatter(quote.CreatedDateTime) : ''
    }));
    excelExport.current?.save(exportData);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
  };

  const pagedData = quotes.slice(skip, skip + PAGE_SIZE);
  if (loading) return <Loader type="pulsing" size="small" />;
  if (!quotes.length) return <div className="k-text-center">No quotes found for this period.</div>;

  return (
    <>
    <ExcelExport
      ref={excelExport}
      columns={EXPORT_COLUMNS}
      fileName={`Quote History ${dataItem.YYYYMM}.xlsx`}
    />
    <Grid
      data={pagedData}
      skip={skip}
      take={PAGE_SIZE}
      total={quotes.length}
      pageable={true}
      onPageChange={handlePageChange}>
      <GridToolbar>
        <Button onClick={exportToExcel}>Excel</Button>
      </GridToolbar>
      <Column field="QuoteID" title="Quote #" cells={{ data: QuoteIDCell }} />
      <Column field="Status" title="Status" />
      <Column field="ThreadID" title="Front" cells={{ data: ThreadIDCell }} />
      <Column field="Source" title="Source" />
      <Column field="Total" title="Total Charges" format="{0:c2}" />
      <Column title="Bid / Decline" cells={{ data: BidCell }} />
      <Column title="Response" cells={{ data: ResultCell }} />
      <Column field="VehicleTypeName" title="Vehicle Type" />
      <Column field="FromLocation" title="From" />
      <Column field="ToLocation" title="To" />
      <Column field="ShipDateTime" title="Ship Date" cells={{ data: DateCell }} />
      <Column field="CreatedDateTime" title="Created" cells={{ data: DateCell }} />
      <Column field="CreatedByUserName" title="By" />
    </Grid>
    </>
  );
};

export default QuoteHistoryDetail;
